/**
 * Sector operations — Unified Sector Hub Configuration.
 * R39 G1 (plan docs/plans/sector-operations.md §5 SO-P101).
 */

import type { NlsKey } from '../../nls/strings_nls'
import { OPS_SECTORS, type OpsSector } from '../../types/ops'
import type { OpsTab } from './opsTabParam'

export interface SectorHubTabConfig {
  readonly key: OpsTab
  readonly labelKey: NlsKey
}

export interface SectorHubConfig {
  readonly titleKey: NlsKey
  readonly subtitleKey: NlsKey
  readonly badge: string
  readonly defaultTab: OpsTab
  readonly tabPrefix: string
  readonly recordHandoffTab: OpsTab
  readonly requestHandoffTab: OpsTab
  readonly sectionTabs: readonly SectorHubTabConfig[]
  readonly setupTabs: readonly SectorHubTabConfig[]
}

function buildCommonSetupTabs(sector: OpsSector): readonly SectorHubTabConfig[] {
  return [
    { key: 'core-places', labelKey: 'NLSOpsTabPlaces' },
    { key: 'core-workforce', labelKey: 'NLSOpsTabWorkforce' },
    { key: 'core-templates', labelKey: 'NLSOpsTabTemplates' },
    { key: 'core-schedules', labelKey: 'NLSOpsTabSchedules' },
    { key: 'core-rosters', labelKey: 'NLSOpsTabRosters' },
    ...(OPS_SECTORS[sector]?.requestsEnabled
      ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
      : []),
    { key: 'core-assets', labelKey: 'NLSOpsTabAssets' },
    ...(OPS_SECTORS[sector]?.bookingsEnabled
      ? [{ key: 'core-bookings' as const, labelKey: 'NLSOpsTabBookings' as const }]
      : []),
    { key: 'core-compliance', labelKey: 'NLSOpsTabCompliance' },
    { key: 'core-whatsapp', labelKey: 'NLSOpsTabWhatsApp' },
    { key: 'core-export', labelKey: 'NLSOpsTabExport' },
    { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
    { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
  ]
}

export const SECTOR_HUB_CONFIGS: Record<OpsSector, SectorHubConfig> = {
  construction: {
    titleKey: 'NLSOpsConstructionTitle',
    subtitleKey: 'NLSOpsConstructionSubtitle',
    badge: 'C',
    defaultTab: 'con-today',
    tabPrefix: 'con-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'con-projects', labelKey: 'NLSOpsTabProjects' },
      { key: 'con-today', labelKey: 'NLSOpsTabTodayDpr' },
      { key: 'con-materials', labelKey: 'NLSOpsTabMaterials' },
      { key: 'con-quality', labelKey: 'NLSOpsTabQuality' },
      { key: 'con-issues', labelKey: 'NLSOpsTabIssues' },
      { key: 'con-reports', labelKey: 'NLSOpsTabReports' },
      { key: 'con-drawings', labelKey: 'NLSOpsTabDrawings' },
      { key: 'con-measure', labelKey: 'NLSOpsTabMeasurement' },
    ],
    setupTabs: buildCommonSetupTabs('construction'),
  },
  hospital: {
    titleKey: 'NLSOpsHospitalTitle',
    subtitleKey: 'NLSOpsHospitalSubtitle',
    badge: 'H',
    defaultTab: 'hos-today',
    tabPrefix: 'hos-',
    recordHandoffTab: 'hos-registers',
    requestHandoffTab: 'hos-requests',
    sectionTabs: [
      { key: 'hos-today', labelKey: 'NLSOpsTabToday' },
      { key: 'hos-requests', labelKey: 'NLSOpsTabRequests' },
      { key: 'hos-equipment', labelKey: 'NLSOpsTabEquipment' },
      { key: 'hos-registers', labelKey: 'NLSOpsTabRegisters' },
      { key: 'hos-people', labelKey: 'NLSOpsTabPeople' },
      { key: 'hos-beds', labelKey: 'NLSOpsTabBeds' },
      { key: 'hos-patients', labelKey: 'NLSOpsTabPatients' },
    ],
    setupTabs: buildCommonSetupTabs('hospital'),
  },
  sites: {
    titleKey: 'NLSOpsSitesTitle',
    subtitleKey: 'NLSOpsSitesSubtitle',
    badge: 'S',
    defaultTab: 'sit-today',
    tabPrefix: 'sit-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'sit-today', labelKey: 'NLSOpsTabSitesToday' },
      { key: 'sit-posts', labelKey: 'NLSOpsTabSitesPosts' },
      { key: 'sit-rounds', labelKey: 'NLSOpsTabSitesRounds' },
      { key: 'sit-incidents', labelKey: 'NLSOpsTabSitesIncidents' },
      { key: 'sit-client', labelKey: 'NLSOpsTabSitesClient' },
      { key: 'sit-wages', labelKey: 'NLSOpsTabSitesWages' },
    ],
    setupTabs: [
      { key: 'core-places', labelKey: 'NLSOpsTabSitesPlaces' },
      { key: 'core-workforce', labelKey: 'NLSOpsTabSitesWorkforce' },
      { key: 'core-templates', labelKey: 'NLSOpsTabSitesTemplates' },
      { key: 'core-schedules', labelKey: 'NLSOpsTabSitesSchedules' },
      { key: 'core-rosters', labelKey: 'NLSOpsTabSitesRosters' },
      ...(OPS_SECTORS.sites?.requestsEnabled
        ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
        : []),
      ...(OPS_SECTORS.sites?.bookingsEnabled
        ? [{ key: 'core-bookings' as const, labelKey: 'NLSOpsTabBookings' as const }]
        : []),
      { key: 'core-import', labelKey: 'NLSOpsTabSitesImport' },
      { key: 'core-whatsapp', labelKey: 'NLSOpsTabSitesWhatsApp' },
      { key: 'core-export', labelKey: 'NLSOpsTabExport' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
  delivery: {
    titleKey: 'NLSOpsDeliveryTitle',
    subtitleKey: 'NLSOpsDeliverySubtitle',
    badge: 'D',
    defaultTab: 'del-today',
    tabPrefix: 'del-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'del-today', labelKey: 'NLSOpsTabDeliveryToday' },
      { key: 'del-engagements', labelKey: 'NLSOpsTabDeliveryEngagements' },
      { key: 'del-requests', labelKey: 'NLSOpsTabDeliveryRequests' },
      { key: 'del-uat', labelKey: 'NLSOpsTabDeliveryUat' },
      { key: 'del-integrations', labelKey: 'NLSOpsTabDeliveryIntegrations' },
      { key: 'del-reports', labelKey: 'NLSOpsTabDeliveryReports' },
    ],
    setupTabs: [
      { key: 'core-templates', labelKey: 'NLSOpsTabTemplates' },
      ...(OPS_SECTORS.delivery?.requestsEnabled
        ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
        : []),
      { key: 'core-import', labelKey: 'NLSOpsTabSitesImport' },
      { key: 'del-export', labelKey: 'NLSOpsTabDeliveryExport' },
      { key: 'core-whatsapp', labelKey: 'NLSOpsTabWhatsApp' },
      { key: 'core-export', labelKey: 'NLSOpsTabExport' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
  shops: {
    titleKey: 'NLSOpsShopsTitle',
    subtitleKey: 'NLSOpsShopsSubtitle',
    badge: 'O',
    defaultTab: 'shp-today',
    tabPrefix: 'shp-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'shp-today', labelKey: 'NLSOpsTabShopsToday' },
      { key: 'shp-cash', labelKey: 'NLSOpsTabShopsCash' },
      { key: 'shp-stock', labelKey: 'NLSOpsTabShopsStock' },
      { key: 'shp-audits', labelKey: 'NLSOpsTabShopsAudits' },
      { key: 'shp-staff', labelKey: 'NLSOpsTabShopsStaff' },
      { key: 'shp-kitchen', labelKey: 'NLSOpsTabShopsKitchen' },
      { key: 'shp-licences', labelKey: 'NLSOpsTabShopsLicences' },
    ],
    setupTabs: [
      { key: 'core-places', labelKey: 'NLSOpsTabShopsPlaces' },
      { key: 'core-workforce', labelKey: 'NLSOpsTabShopsWorkforce' },
      { key: 'core-templates', labelKey: 'NLSOpsTabShopsTemplates' },
      { key: 'core-rosters', labelKey: 'NLSOpsTabShopsRosters' },
      { key: 'core-compliance', labelKey: 'NLSOpsTabShopsCompliance' },
      ...(OPS_SECTORS.shops?.requestsEnabled
        ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
        : []),
      { key: 'core-import', labelKey: 'NLSOpsTabShopsImport' },
      { key: 'core-whatsapp', labelKey: 'NLSOpsTabShopsWhatsApp' },
      { key: 'core-export', labelKey: 'NLSOpsTabExport' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
  stays: {
    titleKey: 'NLSOpsStaysTitle',
    subtitleKey: 'NLSOpsStaysSubtitle',
    badge: 'T',
    defaultTab: 'sty-today',
    tabPrefix: 'sty-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'sty-today', labelKey: 'NLSOpsTabStaysToday' },
      { key: 'sty-board', labelKey: 'NLSOpsTabStaysBoard' },
      { key: 'sty-hk', labelKey: 'NLSOpsTabStaysHousekeeping' },
      { key: 'sty-requests', labelKey: 'NLSOpsTabStaysRequests' },
      { key: 'sty-linen', labelKey: 'NLSOpsTabStaysLinen' },
      { key: 'sty-lost', labelKey: 'NLSOpsTabStaysLost' },
      { key: 'sty-group', labelKey: 'NLSOpsTabStaysGroup' },
      { key: 'sty-extras', labelKey: 'NLSOpsTabStaysExtras' },
      { key: 'sty-pms', labelKey: 'NLSOpsTabStaysPms' },
      { key: 'sty-registers', labelKey: 'NLSOpsTabStaysRegisters' },
    ],
    setupTabs: [
      { key: 'core-places', labelKey: 'NLSOpsTabStaysPlaces' },
      { key: 'core-workforce', labelKey: 'NLSOpsTabStaysWorkforce' },
      { key: 'core-templates', labelKey: 'NLSOpsTabStaysTemplates' },
      { key: 'core-rosters', labelKey: 'NLSOpsTabStaysRosters' },
      { key: 'core-assets', labelKey: 'NLSOpsTabStaysAssets' },
      { key: 'core-compliance', labelKey: 'NLSOpsTabStaysCompliance' },
      { key: 'core-import', labelKey: 'NLSOpsTabStaysImport' },
      { key: 'core-whatsapp', labelKey: 'NLSOpsTabStaysWhatsApp' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
  jobs: {
    titleKey: 'NLSOpsJobsTitle',
    subtitleKey: 'NLSOpsJobsSubtitle',
    badge: 'J',
    defaultTab: 'job-today',
    tabPrefix: 'job-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'job-today', labelKey: 'NLSOpsTabJobsToday' },
      { key: 'job-intake', labelKey: 'NLSOpsTabJobsIntake' },
      { key: 'job-customers', labelKey: 'NLSOpsTabJobsCustomers' },
      { key: 'job-amc', labelKey: 'NLSOpsTabJobsAmc' },
      { key: 'job-parts', labelKey: 'NLSOpsTabJobsParts' },
      { key: 'job-messages', labelKey: 'NLSOpsTabJobsMessages' },
    ],
    setupTabs: [
      { key: 'core-places', labelKey: 'NLSOpsTabJobsPlaces' },
      { key: 'core-workforce', labelKey: 'NLSOpsTabJobsWorkforce' },
      { key: 'core-templates', labelKey: 'NLSOpsTabJobsTemplates' },
      { key: 'core-rosters', labelKey: 'NLSOpsTabJobsRosters' },
      { key: 'core-assets', labelKey: 'NLSOpsTabJobsAssets' },
      { key: 'core-compliance', labelKey: 'NLSOpsTabJobsCompliance' },
      ...(OPS_SECTORS.jobs?.requestsEnabled
        ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
        : []),
      { key: 'core-import', labelKey: 'NLSOpsTabJobsImport' },
      { key: 'core-whatsapp', labelKey: 'NLSOpsTabJobsWhatsApp' },
      { key: 'core-export', labelKey: 'NLSOpsTabExport' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
  clinics: {
    titleKey: 'NLSOpsClinicsTitle',
    subtitleKey: 'NLSOpsClinicsSubtitle',
    badge: 'M',
    defaultTab: 'cli-today',
    tabPrefix: 'cli-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'cli-today', labelKey: 'NLSOpsTabClinicsToday' },
      { key: 'cli-cold-chain', labelKey: 'NLSOpsTabClinicsColdChain' },
      { key: 'cli-waste', labelKey: 'NLSOpsTabClinicsWaste' },
      { key: 'cli-imaging', labelKey: 'NLSOpsTabClinicsImaging' },
      { key: 'cli-samples', labelKey: 'NLSOpsTabClinicsSamples' },
      { key: 'cli-vault', labelKey: 'NLSOpsTabClinicsVault' },
      { key: 'cli-hub', labelKey: 'NLSOpsTabClinicsHub' },
    ],
    setupTabs: [
      { key: 'core-places', labelKey: 'NLSOpsTabClinicsPlaces' },
      { key: 'core-workforce', labelKey: 'NLSOpsTabClinicsWorkforce' },
      { key: 'core-templates', labelKey: 'NLSOpsTabClinicsTemplates' },
      { key: 'core-rosters', labelKey: 'NLSOpsTabClinicsRosters' },
      { key: 'core-assets', labelKey: 'NLSOpsTabClinicsAssets' },
      { key: 'core-compliance', labelKey: 'NLSOpsTabClinicsCompliance' },
      ...(OPS_SECTORS.clinics?.requestsEnabled
        ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
        : []),
      ...(OPS_SECTORS.clinics?.bookingsEnabled
        ? [{ key: 'core-bookings' as const, labelKey: 'NLSOpsTabBookings' as const }]
        : []),
      { key: 'core-import', labelKey: 'NLSOpsTabClinicsImport' },
      { key: 'core-whatsapp', labelKey: 'NLSOpsTabClinicsWhatsApp' },
      { key: 'core-export', labelKey: 'NLSOpsTabExport' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
  plant: {
    titleKey: 'NLSOpsPlantTitle',
    subtitleKey: 'NLSOpsPlantSubtitle',
    badge: 'P',
    defaultTab: 'plt-today',
    tabPrefix: 'plt-',
    recordHandoffTab: 'core-templates',
    requestHandoffTab: 'core-requests',
    sectionTabs: [
      { key: 'plt-today', labelKey: 'NLSOpsTabPlantToday' },
      { key: 'plt-machines', labelKey: 'NLSOpsTabPlantMachines' },
      { key: 'plt-registers', labelKey: 'NLSOpsTabPlantRegisters' },
      { key: 'plt-quality', labelKey: 'NLSOpsTabPlantQuality' },
      { key: 'plt-downtime', labelKey: 'NLSOpsTabPlantDowntime' },
    ],
    setupTabs: [
      { key: 'core-places', labelKey: 'NLSOpsTabPlantPlaces' },
      { key: 'core-workforce', labelKey: 'NLSOpsTabPlantWorkforce' },
      { key: 'core-templates', labelKey: 'NLSOpsTabPlantTemplates' },
      { key: 'core-schedules', labelKey: 'NLSOpsTabPlantSchedules' },
      { key: 'core-rosters', labelKey: 'NLSOpsTabPlantRosters' },
      { key: 'core-assets', labelKey: 'NLSOpsTabPlantAssets' },
      ...(OPS_SECTORS.plant?.requestsEnabled
        ? [{ key: 'core-requests' as const, labelKey: 'NLSOpsTabRequests' as const }]
        : []),
      { key: 'core-import', labelKey: 'NLSOpsTabPlantImport' },
      { key: 'core-export', labelKey: 'NLSOpsTabExport' },
      { key: 'core-devices', labelKey: 'NLSOpsTabDevices' },
      { key: 'core-phone-access', labelKey: 'NLSOpsTabPhoneAccess' },
    ],
  },
}
